// @ts-check
import ms from 'milliseconds'
import { roundToDecimal } from './utils/decimals'

/** @type {LocalAnestheticBase[]} */
const localAnestheticsBase = [
  {
    id: '142440',
    name: 'Lidocaine',
    percent: 0.02,
    halfLife: ms.minutes(120),
    input: 'cartridges',
    variants: [
      {
        id: '142440|1',
        epi: 50,
        color: '#007D59',
        manAbsoluteMax: 500,
        manRatioMax: 7,
        aapdAbsoluteMax: 300,
        aapdRatioMax: 4.4,
        countries: ['US'],
      },
      {
        id: '142440|2',
        epi: 100,
        color: '#FB0019',
        manAbsoluteMax: 500,
        manRatioMax: 7,
        aapdAbsoluteMax: 300,
        aapdRatioMax: 4.4,
        countries: ['US'],
      },
    ],
  },
  {
    id: '2117',
    name: 'Articaine',
    percent: 0.04,
    halfLife: ms.minutes(44.4),
    input: 'cartridges',
    variants: [
      {
        id: '2117|0',
        epi: 100,
        color: '#B28768',
        manAbsoluteMax: null,
        manRatioMax: 7,
        aapdAbsoluteMax: 500,
        aapdRatioMax: 7,
        minAge: 5,
        enableDilute: true,
        ageWarning:
          'Manufacturer recommends not using on patients 4 and under.',
        countries: ['US'],
      },
      {
        id: '2117|1',
        epi: 200,
        color: '#807772',
        manAbsoluteMax: null,
        manRatioMax: 7,
        aapdAbsoluteMax: 500,
        aapdRatioMax: 7,
        minAge: 5,
        enableDilute: true,
        ageWarning:
          'Manufacturer recommends not using on patients 4 and under.',
        countries: ['US'],
      },
    ],
  },
  {
    id: '267396',
    name: 'Bupivacaine',
    percent: 0.005,
    halfLife: ms.hours(3.5),
    nameForChart: '0.5% Bupiv.',
    input: 'cartridges',
    variants: [
      {
        id: '267396|0',
        epi: 200,
        color: '#2063A1',
        manAbsoluteMax: 90,
        manRatioMax: 7,
        minAge: 13,
        enableDilute: true,
        ageWarning: 'Extra caution advised for pediatric patients.',
        countries: ['US'],
      },
    ],
  },
  {
    id: '2557',
    name: 'Prilocaine',
    percent: 0.04,
    halfLife: ms.hours(1.6),
    input: 'cartridges',
    variants: [
      {
        id: '2557|0',
        epi: 200,
        color: '#FFE95E',
        manAbsoluteMax: 600,
        manRatioMax: 8,
        aapdAbsoluteMax: 400,
        aapdRatioMax: 6,
        countries: ['US'],
      },
      {
        id: '2557|1',
        epi: 0,
        color: '#2B251F',
        manAbsoluteMax: 600,
        manRatioMax: 8,
        aapdAbsoluteMax: 300,
        aapdRatioMax: 6,
        countries: ['US'],
      },
    ],
  },
  {
    id: '203185',
    name: 'Mepivacaine',
    percent: 0.03,
    halfLife: ms.hours(1.9),
    input: 'cartridges',
    variants: [
      {
        id: '203185|0',
        color: '#EFC09B',
        manAbsoluteMax: 400,
        manRatioMax: 6.6,
        aapdAbsoluteMax: 300,
        aapdRatioMax: 4.4,
        countries: ['US'],
      },
      {
        id: '700826|0',
        rxcui: '700826',
        subName: '1:20,000 Levonordefrin',
        nameForChart: 'Mepivacaine',
        unitForChart: 'Levonordefrin',
        percent: 0.02,
        color: '#874C41',
        manAbsoluteMax: 400,
        manRatioMax: 6.6,
        aapdAbsoluteMax: 300,
        aapdRatioMax: 4.4,
        countries: ['US'],
      },
    ],
  },
  {
    id: '1726513',
    name: 'Exparel™',
    altName: 'Bupivacaine liposome susp.',
    reminder:
      'Never to be injected within 20 minutes of any other kind of non-Bupivacaine local anesthetic, nor is any other local anesthetic to be given 96 hours after Exparel.',
    percent: 0.0133,
    halfLife: ms.hours(24),
    input: 'mg',
    variants: [
      {
        id: '1726513|0',
        color: '#044F56',
        minAge: 6,
        ageWarning: 'FDA approved for age 6+ only.',
        countries: ['US'],
      },
    ],
  },
  {
    id: '1856568',
    name: 'OraVerse™',
    altName: 'Phentolamine Mesylate',
    reminder:
      'Should only follow local anesthetics that contain a vasoconstrictor.',
    percent: roundToDecimal(0.4 / 1.7 / 1000, 7),
    input: 'cartridges',
    variants: [
      {
        id: '1856568|0',
        subName: '0.4 mg/1.7 mL',
        color: '#B2EB87',
        minAge: 3,
        noEpiWarning:
          'No doses with epinephrine have been recorded. Should only be given after locals with a vasoconstrictor.',
        ageWarning:
          'The safety and efficacy of Phentolamine Mesylate has not been established in patients younger than 3 years.',
        // very important!
        skipForToxicity: true,
        countries: ['US'],
      },
    ],
  },
]

/** @type {(country?: 'US' | 'AUS') => LocalAnestheticVariantProcessed[]} */
export const getLocalAnestheticVariantsForCountry = (country = 'US') =>
  localAnestheticsBase.reduce((result, drug) => {
    const { variants, ...rest } = drug
    variants.forEach(variant => {
      const { countries, ...variantRest } = variant
      if (!countries.includes(country)) return
      result.push({
        ...rest,
        rxcui: drug.id,
        ...variantRest,
      })
    })
    return result
  }, [])

/** @type {Record<string, LocalAnestheticVariantProcessed>} */
export const localAnestheticLookupById = getLocalAnestheticVariantsForCountry().reduce(
  (result, drug) => {
    result[drug.id] = drug
    return result
  },
  {}
)
